import React, { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import I18n from "i18n-js";
import _ from "lodash";

import api from "../../api";

import BaseModal from "../../modals/BaseModal";
import Textarea from "../../commons/Textarea";
import ClearButton from "../../commons/ClearButton";

import TrainingCourseSuggestionList from "../components/TrainingCourseSuggestionList";

const NewTrainingCourseModal = ({ onClose }) => {

  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  
  const [skills, setSkills] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestion, setSuggestion] = useState(null);

  let data = { interval: null };

  useEffect(() => {
    return () => {
      if (data?.interval)
        clearInterval(data?.interval);
    }
  }, []);

  const getSuggestions = () => {
    if (!skills) return;
    
    setLoading(true);

    api.post("/training-courses/suggestions", { skills })
      .then(res => {
        const suggestions = res?.data?.suggestions
        setSuggestions(suggestions);
        setLoading(false);
      })
      .catch(handleRequestError);
  }

  const createTrainingCourse = () => {
    if (!skills || !suggestion) return;
    
    setLoading(true);

    api.post("/training-courses", { skills, ...suggestion })
      .then(res => {
        data = {
          ...data,
          uuid: res?.data?.training_course?.uuid,
          redirectTo: res?.data?.redirectTo
        };
        
        poolTrainingCourseStatus();
      })
      .catch(handleRequestError);
  }

  const poolTrainingCourseStatus = () => {
    data.interval = setInterval(async () => {
      try {
        const res = await api.get(`/training-courses/${data?.uuid}/status`);
        
        if (res?.data?.training_course?.status === "completed") {
          handleCreationSuccess();
        }
      } catch (error) {
        handleRequestError(error);
      }
    }, 3000);
  }

  const handleCreationSuccess = () => {
    clearInterval(data?.interval);
    toast.success(<b>{I18n.t("common.created_successfully")}</b>);
    window.location.href = data?.redirectTo;
  }

  const handleRequestError = (err) => {
    if (err.response && err.response.data && err.response.data.message) {
      toast.error(<b>{err.response.data.message}</b>);
    } else {
      toast.error(<b>{I18n.t("common.errors.unexpected_error")}</b>);
    }
    setLoading(false);
    console.error(err);
  }

  const handleSkillsChange = (event) => {
    event.preventDefault();
    setSkills(event.target.value);
  }

  const handleClear = () => {
    setSkills("");
    setSuggestions  ([]);
    setSuggestion(null);
  }

  const showClear = skills?.length > 0 || suggestions?.length > 0;

  const renderBody = () => {
    return (
      <div className="new-training-course-modal-content">
        <AnimatePresence>
          {showClear ? <ClearButton disabled={loading} onClick={handleClear} /> : null}
        </AnimatePresence>
        <Textarea
          label={I18n.t("training_courses.modals.new_training_course.inputs.skills.label")}
          placeholder={I18n.t("training_courses.modals.new_training_course.inputs.skills.placeholder")}
          value={skills}
          rows={3}
          disabled={loading}
          onChange={handleSkillsChange}
        />
        <TrainingCourseSuggestionList
          items={suggestions}
          loading={loading}
          onItemSelected={setSuggestion}
        />
      </div>
    );
  }

  return (
    <BaseModal
      open={open}
      name="new-training-course"
      title={I18n.t("training_courses.modals.new_training_course.title")}
      introduction={I18n.t("training_courses.modals.new_training_course.introduction")}
      body={renderBody()}
      confirm={{
        show: true,
        disabled: _.isEmpty(skills),
        title: I18n.t("training_courses.modals.new_training_course.buttons.create.title"),
        loading: loading
      }}
      onConfirm={suggestion ? createTrainingCourse : getSuggestions}
      onClose={() => setOpen(false)}
      onCloseAnimationEnd={onClose}
    />
  );
}

export default NewTrainingCourseModal;
